/**
 * Lightweight Before / After compare (PRD.164).
 *
 * - Two slots (Before / After) with "Choose Existing" + "Upload New".
 * - Reuses the encounter's patient_files store — no separate media pipeline.
 * - Renders side-by-side compare when both slots are filled.
 * - No merged image artifact is produced; this is presentation-only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientFilesByEncounter,
  qEncounterVisualCharts,
  type PatientFileRow,
  type EncounterVisualChartRow,
} from '@helios/data-access';
import { uploadToStorage } from '../../../lib/upload-to-storage';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  /** Open a specific charting record (e.g. from clicking a card in All). */
  initialChartId?: string | null;
}

type Side = 'before' | 'after';

// ─── Component ───

export function BeforeAfterCompare({ patientId, encounterId, visitId, initialChartId }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );

  const images = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);
  const byId = useMemo(() => {
    const map = new Map<string, PatientFileRow>();
    for (const img of images) map.set(img.id, img);
    return map;
  }, [images]);

  type Picked = { id: string; url: string } | null;
  const [before, setBefore] = useState<Picked>(null);
  const [after, setAfter] = useState<Picked>(null);
  const [pickerSide, setPickerSide] = useState<Side | null>(null);

  // ─── Persistence (PRD.165) ───
  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId),
  );

  // User can switch between saved pairs via the strip.
  // '__new__' = explicitly starting a fresh comparison (don't fallback to existing).
  const [selectedChartId, setSelectedChartId] = useState<string | '__new__' | null>(
    initialChartId ?? null,
  );

  // Resolve target chart: prefer user-selected, then initialChartId prop, then newest.
  // When '__new__', resolve to null so hydration doesn't overwrite the blank slate.
  const targetChart = useMemo(() => {
    if (selectedChartId === '__new__') return null;
    if (selectedChartId) {
      const match = chartRows.find((c) => c.id === selectedChartId);
      if (match) return match;
    }
    if (initialChartId) {
      const match = chartRows.find((c) => c.id === initialChartId);
      if (match) return match;
    }
    return chartRows[0] ?? null;
  }, [chartRows, selectedChartId, initialChartId]);

  const persistedChartIdRef = useRef<string | null>(null);
  const hydratedRef = useRef(false);
  const hydratedForChartRef = useRef<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Hydrate from the target chart. Re-hydrate if the target chart ID changes
  // (e.g. user clicked a different card in All).
  useEffect(() => {
    if (!targetChart) return;
    if (hydratedRef.current && hydratedForChartRef.current === targetChart.id) return;
    const beforeRow = targetChart.before_file_id ? byId.get(targetChart.before_file_id) : null;
    const afterRow = targetChart.after_file_id ? byId.get(targetChart.after_file_id) : null;
    const beforeReady = !targetChart.before_file_id || beforeRow;
    const afterReady = !targetChart.after_file_id || afterRow;
    if (!beforeReady || !afterReady) return;
    setBefore(beforeRow?.url ? { id: beforeRow.id, url: beforeRow.url } : null);
    setAfter(afterRow?.url ? { id: afterRow.id, url: afterRow.url } : null);
    if (targetChart.label === 'side-by-side' || targetChart.label === 'slider') {
      setCompareMode(targetChart.label as CompareMode);
    }
    persistedChartIdRef.current = targetChart.id;
    hydratedRef.current = true;
    hydratedForChartRef.current = targetChart.id;
    setTouched(false);
  }, [targetChart, byId]);

  const startNewComparison = () => {
    setBefore(null);
    setAfter(null);
    persistedChartIdRef.current = null;
    setSelectedChartId('__new__');
    hydratedRef.current = true;
    hydratedForChartRef.current = '__new__';
    setTouched(false);
  };

  const beforeImg = before
    ? (byId.get(before.id) ??
      ({ id: before.id, url: before.url, filename: null } as unknown as PatientFileRow))
    : null;
  const afterImg = after
    ? (byId.get(after.id) ??
      ({ id: after.id, url: after.url, filename: null } as unknown as PatientFileRow))
    : null;

  // ─── Save state ───
  const beforeKey = before?.id ?? null;
  const afterKey = after?.id ?? null;
  const savedBeforeKey = targetChart?.before_file_id ?? null;
  const savedAfterKey = targetChart?.after_file_id ?? null;
  const isDirty = touched && (beforeKey !== savedBeforeKey || afterKey !== savedAfterKey);
  const [saving, setSaving] = useState(false);
  type CompareMode = 'side-by-side' | 'slider';
  const [compareMode, setCompareMode] = useState<CompareMode>('slider');

  const handleSave = async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const existingId = persistedChartIdRef.current;
      if (existingId) {
        await db.execute(
          `UPDATE encounter_visual_charts
             SET before_file_id = ?, after_file_id = ?, label = ?, updated_at = ?
           WHERE id = ?`,
          [beforeKey, afterKey, compareMode, now, existingId],
        );
      } else if (beforeKey || afterKey) {
        const id = crypto.randomUUID();
        persistedChartIdRef.current = id;
        await db.execute(
          `INSERT INTO encounter_visual_charts
            (id, org_id, clinic_id, patient_id, encounter_id, visit_id, chart_type, before_file_id, after_file_id, label, created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounterId,
            visitId || null,
            'before_after',
            beforeKey,
            afterKey,
            compareMode,
            null,
            now,
            now,
          ],
        );
      }
      setTouched(false);
    } catch (err) {
      console.error('[BeforeAfter] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const assign = (side: Side, picked: Picked) => {
    setTouched(true);
    if (side === 'before') setBefore(picked);
    else setAfter(picked);
  };

  const assignById = (side: Side, id: string) => {
    const row = byId.get(id);
    if (!row || !row.url) return;
    assign(side, { id, url: row.url });
  };

  const swap = () => {
    setTouched(true);
    const b = before;
    setBefore(after);
    setAfter(b);
  };

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      {/* Saved pairs strip — browse all saved comparisons */}
      {chartRows.length > 0 && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: 'var(--text-muted)',
              marginBottom: 8,
            }}
          >
            Saved Comparisons
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {chartRows
              .filter((c) => c.before_file_id || c.after_file_id)
              .map((chart) => {
                const isActive = targetChart?.id === chart.id;
                const bUrl = chart.before_file_id
                  ? (byId.get(chart.before_file_id)?.url ?? null)
                  : null;
                const aUrl = chart.after_file_id
                  ? (byId.get(chart.after_file_id)?.url ?? null)
                  : null;

                return (
                  <button
                    key={chart.id}
                    onClick={() => {
                      setSelectedChartId(chart.id);
                      hydratedForChartRef.current = null;
                      hydratedRef.current = false;
                      setTouched(false);
                    }}
                    style={{
                      width: 80,
                      height: 80,
                      flexShrink: 0,
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      border: isActive
                        ? '2px solid var(--color-primary)'
                        : '1px solid var(--border)',
                      background: 'var(--bg-surface-2)',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      position: 'relative',
                    }}
                  >
                    <MiniHalf url={bUrl} />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '50%',
                        width: 1,
                        background: 'rgba(255,255,255,0.6)',
                        zIndex: 1,
                      }}
                    />
                    <MiniHalf url={aUrl} />
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Header row with actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Assign a Before and After image to compare.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={startNewComparison}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <NavIcon name="plus" /> New Comparison
          </button>
          {/* Dirty / saved indicator */}
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: isDirty ? 'var(--color-warning-text)' : 'var(--text-muted)',
              marginRight: 4,
            }}
          >
            {isDirty ? 'Unsaved changes' : persistedChartIdRef.current ? 'Saved' : ''}
          </span>
          {beforeImg && afterImg && (
            <button
              onClick={swap}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <NavIcon name="list" /> Swap
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${!isDirty || saving ? 'var(--border)' : 'var(--color-primary)'}`,
              background: !isDirty || saving ? 'var(--bg-surface)' : 'var(--color-primary)',
              color: !isDirty || saving ? 'var(--text-muted)' : '#fff',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: !isDirty || saving ? 'default' : 'pointer',
              opacity: !isDirty ? 0.5 : 1,
            }}
          >
            <NavIcon name="check" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {beforeImg?.url && afterImg?.url ? (
        compareMode === 'slider' ? (
          <div
            style={{
              marginBottom: 'var(--sp-2)',
              position: 'relative',
            }}
          >
            {/* Slider */}
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                border: '1px solid var(--border)',
                aspectRatio: '16 / 10',
                background: 'var(--bg-surface-2)',
              }}
            >
              <ReactCompareSlider
                itemOne={
                  <ReactCompareSliderImage
                    src={beforeImg.url}
                    alt="Before"
                    style={{ objectFit: 'contain', background: 'var(--bg-surface-2)' }}
                  />
                }
                itemTwo={
                  <ReactCompareSliderImage
                    src={afterImg.url}
                    alt="After"
                    style={{ objectFit: 'contain', background: 'var(--bg-surface-2)' }}
                  />
                }
                style={{ height: '100%', width: '100%' }}
              />
            </div>
            {/* Corner action bars — positioned over the outer wrapper so they
                sit above the slider's overflow:hidden clipping boundary */}
            <CornerActions
              side="left"
              label="Before"
              orgId={orgId}
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId}
              db={db}
              onAssign={(picked) => assign('before', picked)}
              onClear={() => assign('before', null)}
              onOpenPicker={() => setPickerSide('before')}
            />
            <CornerActions
              side="right"
              label="After"
              orgId={orgId}
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId}
              db={db}
              onAssign={(picked) => assign('after', picked)}
              onClear={() => assign('after', null)}
              onOpenPicker={() => setPickerSide('after')}
            />
          </div>
        ) : (
          /* Clean side-by-side: label above each image, no card chrome */
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              marginBottom: 'var(--sp-2)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--border)',
              background: '#000',
            }}
          >
            <SideBySidePane label="BEFORE" url={beforeImg.url} />
            <SideBySidePane label="AFTER" url={afterImg.url} />
          </div>
        )
      ) : (
        /* Empty / partial state — same visual container with dark corner bars */
        <div
          style={{
            position: 'relative',
            marginBottom: 'var(--sp-2)',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface-2)',
            }}
          >
            <div
              style={{
                aspectRatio: '3 / 4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {beforeImg?.url ? (
                <img
                  src={beforeImg.url}
                  alt="Before"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  Select a Before image
                </span>
              )}
            </div>
            <div
              style={{
                aspectRatio: '3 / 4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {afterImg?.url ? (
                <img
                  src={afterImg.url}
                  alt="After"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  Select an After image
                </span>
              )}
            </div>
          </div>
          <CornerActions
            side="left"
            label="Before"
            orgId={orgId}
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            db={db}
            onAssign={(picked) => assign('before', picked)}
            onClear={() => assign('before', null)}
            onOpenPicker={() => setPickerSide('before')}
          />
          <CornerActions
            side="right"
            label="After"
            orgId={orgId}
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            db={db}
            onAssign={(picked) => assign('after', picked)}
            onClear={() => assign('after', null)}
            onOpenPicker={() => setPickerSide('after')}
          />
        </div>
      )}

      {/* Mode toggle — shown when both slots are filled */}
      {beforeImg?.url && afterImg?.url && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0,
            marginTop: 'var(--sp-2)',
          }}
        >
          <button
            onClick={() => setCompareMode('side-by-side')}
            style={{
              padding: '6px 14px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: compareMode === 'side-by-side' ? 600 : 500,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm) 0 0 var(--radius-sm)',
              background:
                compareMode === 'side-by-side' ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: compareMode === 'side-by-side' ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <NavIcon name="layout-grid" /> Side by Side
          </button>
          <button
            onClick={() => setCompareMode('slider')}
            style={{
              padding: '6px 14px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: compareMode === 'slider' ? 600 : 500,
              border: '1px solid var(--border)',
              borderLeft: 'none',
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              background: compareMode === 'slider' ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: compareMode === 'slider' ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <NavIcon name="list" /> Slider
          </button>
        </div>
      )}

      {/* Existing-image picker */}
      {pickerSide && (
        <ExistingImagePicker
          images={images}
          excludeId={pickerSide === 'before' ? (after?.id ?? null) : (before?.id ?? null)}
          onPick={(id) => {
            assignById(pickerSide, id);
            setPickerSide(null);
          }}
          onClose={() => setPickerSide(null)}
          side={pickerSide}
        />
      )}
    </div>
  );
}

// ─── Existing image picker (modal) ───

function ExistingImagePicker({
  images,
  excludeId,
  onPick,
  onClose,
  side,
}: {
  images: PatientFileRow[];
  excludeId: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
  side: Side;
}) {
  const pickable = images.filter((img) => img.id !== excludeId);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 998,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxWidth: '92vw',
          maxHeight: '80vh',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
            Choose {side === 'before' ? 'Before' : 'After'} image
          </span>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4)' }}>
          {pickable.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-6)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No existing images for this visit. Use "Upload new" instead.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 8,
              }}
            >
              {pickable.map((img) => (
                <button
                  key={img.id}
                  onClick={() => onPick(img.id)}
                  style={{
                    padding: 0,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--bg-surface-2)',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    aspectRatio: '1',
                  }}
                >
                  <img
                    src={img.url!}
                    alt={img.filename ?? ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Corner action bar for the slider ───
// Shared upload/picker/clear row per side overlayed on the slider corners.

interface CornerActionsProps {
  side: 'left' | 'right';
  label: string;
  orgId: string;
  patientId: string;
  encounterId: string;
  visitId: string;
  db: ReturnType<typeof usePowerSyncDb>['db'];
  onAssign: (picked: { id: string; url: string }) => void;
  onClear: () => void;
  onOpenPicker: () => void;
}

function CornerActions({
  side,
  label,
  orgId,
  patientId,
  encounterId,
  visitId,
  db,
  onAssign,
  onClear,
  onOpenPicker,
}: CornerActionsProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !db) return;
    setUploading(true);
    try {
      const fileId = crypto.randomUUID();
      const { downloadUrl } = await uploadToStorage({ orgId, patientId, file, fileId });
      const now = new Date().toISOString();
      await db.execute(
        `INSERT INTO patient_files
          (id, org_id, clinic_id, patient_id, encounter_id, visit_id, kind, filename, url, tag, mime_type, description, starred, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          fileId,
          orgId,
          null,
          patientId,
          encounterId,
          visitId || null,
          'image',
          file.name,
          downloadUrl,
          'before_after',
          file.type || null,
          null,
          0,
          now,
          now,
        ],
      );
      onAssign({ id: fileId, url: downloadUrl });
    } catch (err) {
      console.error('[BeforeAfter] upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        [side]: 8,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 6px',
        borderRadius: 'var(--radius-sm)',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <span
        style={{
          color: '#fff',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          padding: '0 4px',
        }}
      >
        {label}
      </span>
      <CornerBtn title="Choose existing" onClick={onOpenPicker}>
        <NavIcon name="folder-open" />
      </CornerBtn>
      <CornerBtn
        title={uploading ? 'Uploading…' : 'Upload new'}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        <NavIcon name="plus" />
      </CornerBtn>
      <CornerBtn title="Clear" onClick={onClear}>
        <NavIcon name="x" />
      </CornerBtn>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />
    </div>
  );
}

function CornerBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 22,
        height: 22,
        borderRadius: 'var(--radius-sm)',
        border: 'none',
        background: 'rgba(255,255,255,0.12)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
      }}
    >
      {children}
    </button>
  );
}

function SideBySidePane({ label, url }: { label: string; url: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', background: '#000' }}>
      <div
        style={{
          padding: '8px 0',
          textAlign: 'center',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          background: '#000',
        }}
      >
        {label}
      </div>
      <img
        src={url}
        alt={label}
        style={{
          width: '100%',
          aspectRatio: '3 / 4',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </div>
  );
}

function MiniHalf({ url }: { url: string | null }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return <div style={{ width: '100%', height: '100%', background: 'var(--bg-surface-2)' }} />;
}
